import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Linkedin, ArrowRight } from 'lucide-react';
import logo from '../../assets/logo.jpg';
import { siteInfo, schoolDivisions } from '../../data/schoolInfo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'About Us', href: '/about' },
    { label: 'Admissions', href: '/admissions' },
    { label: 'Gallery', href: '/gallery' },
    { label: 'News & Events', href: '/news' },
    { label: 'Contact', href: '/contact' },
  ];

  const montessoriLinks = [
    { label: 'Overview', href: '/montessori' },
    { label: 'Curriculum', href: '/montessori/curriculum' },
    { label: 'Facilities', href: '/montessori/facilities' },
    { label: 'Gallery', href: '/montessori/gallery' },
  ];

  const collegeLinks = [
    { label: 'Overview', href: '/college' },
    { label: 'Academics', href: '/college/academics' },
    { label: 'Extracurriculars', href: '/college/extracurriculars' },
    { label: 'Achievements', href: '/college/achievements' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Newsletter Section */}
      <div className="bg-primary-600">
        <div className="container-custom py-8 md:py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-lg md:text-xl font-bold text-white mb-1 md:mb-2">Stay Updated</h3>
              <p className="text-sm md:text-base text-primary-100">Subscribe to our newsletter for latest news and updates.</p>
            </div>
            <form className="flex flex-col sm:flex-row w-full md:w-auto gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full sm:w-64 md:w-72 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm"
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-5 py-3 bg-white text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                Subscribe
                <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container-custom py-10 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-4 md:mb-6">
              <img src={logo} alt="Stirret Schools" className="h-12 md:h-16 w-auto" />
              <div>
                <span className="block text-lg md:text-xl font-bold text-white">Stirret Schools</span>
                <span className="block text-xs md:text-sm text-gray-400">{siteInfo.tagline}</span>
              </div>
            </Link>
            <p className="text-sm md:text-base text-gray-400 mb-4 md:mb-6 max-w-sm hidden md:block">
              {siteInfo.description}. We nurture curious minds and bold thinkers from early childhood through secondary education.
            </p>
            <div className="space-y-3">
              <a href={`tel:${siteInfo.contact.phone}`} className="flex items-center gap-3 hover:text-white transition-colors">
                <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                  <Phone size={18} />
                </div>
                {siteInfo.contact.phone}
              </a>
              <a href={`mailto:${siteInfo.contact.email}`} className="flex items-center gap-3 hover:text-white transition-colors">
                <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                  <Mail size={18} />
                </div>
                {siteInfo.contact.email}
              </a>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} />
                </div>
                <span>{siteInfo.contact.address}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Montessori */}
          <div>
            <h4 className="text-white font-semibold mb-6">Montessori & Primary</h4>
            <ul className="space-y-3">
              {montessoriLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* College */}
          <div>
            <h4 className="text-white font-semibold mb-6">Stirret College</h4>
            <ul className="space-y-3">
              {collegeLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Links */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">Follow us:</span>
              <div className="flex items-center gap-3">
                <a
                  href={siteInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-primary-600 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href={siteInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-primary-600 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href={siteInfo.social.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-primary-600 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter size={18} />
                </a>
                <a
                  href={siteInfo.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center hover:bg-primary-600 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <p>&copy; {currentYear} Stirret Schools. All rights reserved.</p>
            <p>
              Designed by{' '}
              <a
                href="https://cizonet.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 transition-colors font-medium"
              >
                Cizonet Solutions
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
